import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  basicInfoQuestions,
  allowanceAndWithholdingQuestions,
  allowanceRateQuestion2025,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function Version2025({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFNameMiddleInitial = () => {
    const fName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name || '';
    return fName + middleInitial;
  };
  prefilledData.employee_first_name_middle_initial = formatFNameMiddleInitial();

  const basicInfoForm = useRef();
  const basicInfoInitQ = getInitialQuestions(basicInfoQuestions);
  const [basicInfo, setBasicInfo] = useState(basicInfoInitQ);

  const allowanceRateForm = useRef();
  const allowanceRateInitQ = getInitialQuestions(allowanceRateQuestion2025);
  const [allowanceRate, setAllowanceRate] = useState(allowanceRateInitQ);

  const allowanceAndWithholdingForm = useRef();
  const allowanceAndWithholdingInitQ = getInitialQuestions(allowanceAndWithholdingQuestions);
  const [allowanceAndWithholding, setAllowanceAndWithholding] = useState(
    allowanceAndWithholdingInitQ,
  );

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || false);

  const renderBasicInfoForm = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={basicInfo}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = basicInfo.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setBasicInfo(questions);
        }}
        ref={basicInfoForm}
      />
      <Flex column>
        <Text style={{ fontWeight: '600', fontSize: '14px' }} mb="10px">
          Exemption from withholding
        </Text>
        <S.QuestionLabel style={{ marginBottom: '7px' }} isRequired>
          To qualify for exempt status you must verify with the Kansas Department of Revenue that:
          {' '}
          <br />
          1) last year you had the right to a refund of all STATE income tax withheld because
          you had no tax liability; and
          {' '}
          <br />
          2) this year you will receive a full refund of all STATE income tax withheld because
          you will have no tax liability.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center" pb="15px">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              I claim exemption from withholding.
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              I do not claim exemption from withholding
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const renderAllowancesForm = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={allowanceRate}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = allowanceRate.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAllowanceRate(questions);
        }}
        ref={allowanceRateForm}
      />
    </div>
  );

  const renderAllowanceAndWithholdingForm = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={allowanceAndWithholding}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = allowanceAndWithholding.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAllowanceAndWithholding(questions);
        }}
        ref={allowanceAndWithholdingForm}
      />
    </div>
  );

  const handleValidation = () => {
    if (basicInfoForm && basicInfoForm.current
      && basicInfoForm.current.validate((error) => !!error)) {
      return true;
    }

    if (allowanceAndWithholdingForm && allowanceAndWithholdingForm.current
      && allowanceAndWithholdingForm.current.validate((error) => !!error)) {
      return true;
    }

    if (allowanceRateForm && allowanceRateForm.current
      && allowanceRateForm.current.validate((error) => !!error)) {
      return true;
    }

    return false;
  };

  const handleSubmission = () => {
    const hasError = handleValidation();

    if (hasError) return;

    const basicInfoData = basicInfo
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    const exemptionStatus = { is_exempt: isExemptFromWithholding };

    let allowanceAndWithholdingData = resetInitialQuestions(allowanceAndWithholdingQuestions)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    let allowanceRateData = resetInitialQuestions(allowanceRateQuestion2025)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

    if (!isExemptFromWithholding) {
      allowanceAndWithholdingData = allowanceAndWithholding
        .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
      allowanceRateData = allowanceRate
        .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    }

    const payload = {
      ...basicInfoData,
      ...exemptionStatus,
      ...allowanceRateData,
      ...allowanceAndWithholdingData,
    };

    saveFormData(payload, () => {
      setCurrentStep(STEPS.step2);
      scrollTop();
    });
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderBasicInfoForm()}
          {!isExemptFromWithholding && renderAllowancesForm()}
          {!isExemptFromWithholding && renderAllowanceAndWithholdingForm()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={handleSubmission}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderBasicInfoForm(true)}
        {!isExemptFromWithholding && renderAllowancesForm(true)}
        {!isExemptFromWithholding && renderAllowanceAndWithholdingForm(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default Version2025;
